import { editorialStatusBlock, button } from "../../design/components.js";
import { mountPaperMap, type MapHandle } from "../now/map-view.js";
import { SUPPORTED_BBOX } from "../../map/tile-source.js";

export interface HomeCtx {
  t: (k: string, p?: Record<string, string | number>) => string;
  defaultCell: string;
  /** Seed radius (km) — used when re-opening the picker on an existing home. Default 15. */
  initialRadiusKm?: number;
  /** Compact embed (add/edit-place modal — which already carries its own title): drop the
   *  big onboarding step header + display headline so the picker fits the dialog and its
   *  action buttons stay reachable. */
  compact?: boolean;
  /** Onboarding only: try geolocation on mount and jump the map there, so a first-time
   *  user lands on their actual location. Falls back to defaultCell (Lisbon) if denied
   *  or unavailable. */
  geolocate?: () => Promise<string | null>;
  /** Map factory (DI for tests; defaults to mountPaperMap). */
  mount?: typeof mountPaperMap;
  onConfirm: (h: { cell: string; radiusKm: number; placeName?: string }) => void;
  onBrowse: () => void;
}

/** Onboarding screen 3 — Where's home? A fixed center pin marks the chosen spot: pan
 *  the map so the pin sits on your home (home = the res-8 cell under the pin). The
 *  radius slider (5–50 km) tints the affected H3 cells. The map mount is WebGL-guarded
 *  (jsdom has no WebGL → slider-only fallback; the cell stays at defaultCell). */
export function homeScreen(ctx: HomeCtx): HTMLElement {
  const root = document.createElement("section");
  root.className = "screen onboarding-home";
  if (ctx.compact) {
    // Embedded in a modal that already has a title — just a one-line instruction.
    const hint = document.createElement("p");
    hint.className = "bx-microlabel onboarding-home__hint";
    hint.textContent = ctx.t("onboarding.home.pickHint");
    root.append(hint);
  } else {
    root.append(
      editorialStatusBlock({ microlabel: ctx.t("onboarding.home.step"), headline: ctx.t("onboarding.home.title") }),
    );
  }

  // Onboarding (geolocate given): start on the served-extract OVERVIEW with NO cell chosen —
  // confirm stays disabled until a location fix or a manual pan picks one — instead of a
  // street-zoom on a default city the user then has to drag away from. Add/edit place
  // (no geolocate): the old behaviour, seeded on defaultCell.
  const suggesting = !!ctx.geolocate;
  let cell: string | undefined = suggesting ? undefined : ctx.defaultCell;
  let radiusKm = ctx.initialRadiusKm ?? 15;
  if (suggesting) root.setAttribute("data-initial-view", "overview");

  const mapMount = document.createElement("div");
  mapMount.className = "home-map";
  mapMount.setAttribute("aria-label", ctx.t("onboarding.home.title"));
  let map: MapHandle | undefined;
  const syncConfirm = (): void => { (confirm as HTMLButtonElement).disabled = !cell; };
  // Center-pin: the map pans under the fixed pin; moveend fires onCenterCell with the
  // res-8 cell, and we re-tint the radius around the new center.
  const mount = ctx.mount ?? mountPaperMap;
  map = mount(mapMount, {
    ...(suggesting ? { initialBounds: SUPPORTED_BBOX } : { centerCell: cell }),
    centerPin: true,
    onCenterCell: (c) => { cell = c; map?.setPlaces([{ cell, radiusKm }]); syncConfirm(); },
  });
  root.append(mapMount);

  // Location status line (onboarding only): finding… → pin set / unavailable.
  const status = document.createElement("p");
  status.className = "bx-microlabel onboarding-home__status";
  status.setAttribute("data-location-status", "");
  if (suggesting) {
    status.textContent = ctx.t("onboarding.home.locating");
    root.append(status);
  }

  // First-run geolocation: the OS prompt fires here (JIT). On a fix, the pin goes to the
  // current location (the suggestion) and confirm enables; on deny/timeout we stay on the
  // overview and ask the user to move the map. (In jsdom the map is inert, so set the cell directly too.)
  if (ctx.geolocate) {
    void ctx.geolocate().then((c) => {
      if (!c) { status.textContent = ctx.t("onboarding.home.unavailable"); return; }
      cell = c;
      map?.setCenter(c);
      map?.setPlaces([{ cell, radiusKm }]);
      status.textContent = ctx.t("onboarding.home.located");
      syncConfirm();
    });
  }

  const slider = document.createElement("input");
  slider.type = "range";
  slider.min = "5";
  slider.max = "50";
  slider.value = String(radiusKm);
  slider.setAttribute("data-radius", "");
  slider.setAttribute("aria-label", ctx.t("onboarding.home.radius"));

  const radiusLabel = document.createElement("p");
  radiusLabel.className = "bx-microlabel";
  const renderRadius = () => {
    radiusLabel.textContent = ctx.t("onboarding.home.km", { n: radiusKm });
    if (cell) map?.setPlaces([{ cell, radiusKm }]);
  };
  slider.addEventListener("input", () => {
    radiusKm = Number(slider.value);
    renderRadius();
  });
  renderRadius();
  root.append(slider, radiusLabel);

  const confirm = button({
    label: ctx.t("onboarding.home.confirm"),
    variant: "primary",
    onClick: () => { if (cell) ctx.onConfirm({ cell, radiusKm }); },
  });
  confirm.setAttribute("data-action", "confirm");
  syncConfirm();
  confirm.setAttribute("aria-label", ctx.t("onboarding.home.confirm"));

  const browse = button({ label: ctx.t("onboarding.home.browse"), variant: "secondary", onClick: ctx.onBrowse });
  browse.setAttribute("data-action", "browse");
  browse.setAttribute("aria-label", ctx.t("onboarding.home.browse"));

  root.append(confirm, browse);
  return root;
}
